import React from "react";
import { Container, Typography, Box, Divider } from "@mui/material";

export const TermsAndConditionsPage: React.FC = () => {
    return (
        <>
            <Typography variant="h4" gutterBottom>
                <title>Terms and Conditions | Relapka</title>
                <meta
                    name="description"
                    content="Terms and Conditions for using the Relapka real estate information platform."
                />
            </Typography>
            <Container maxWidth="md" sx={{py: 6}}>
                <Typography variant="h4" gutterBottom>
                    Terms and Conditions
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last updated: February 7, 2026
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography component={"p"}>
                    Welcome to <strong>Relapka</strong> (the “Website”, “Service”,
                    “we”, “us”, or “our”), accessible at{" "}
                    <strong>https://relapka.com</strong>.
                </Typography>

                <Typography component={"p"}>
                    By accessing or using this Website, you agree to be bound by these Terms
                    and Conditions (“Terms”). If you do not agree with these Terms, please
                    do not use the Website.
                </Typography>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        1. Purpose of the Website
                    </Typography>
                    <Typography component={"p"}>
                        The Website provides an informational platform for real estate
                        listings located in Georgia. The Service includes:
                    </Typography>
                    <ul>
                        <li>Display and storage of real estate listings</li>
                        <li>
                            Advanced search and filtering tools (including map-based search and
                            price per square meter)
                        </li>
                        <li>Generation of PDF reports based on available data</li>
                        <li>Notifications about new or updated listings</li>
                    </ul>
                    <Typography component={"p"}>
                        The Website does not act as a real estate agency, broker, or
                        intermediary and does not participate in negotiations,
                        transactions, or contracts between users.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        2. Nature of Listings and Information Accuracy
                    </Typography>
                    <Typography component={"p"}>
                        All listings and related information are provided by third parties or
                        collected from publicly available sources.
                    </Typography>
                    <Typography component={"p"}>
                        We do not guarantee the accuracy, completeness, legality, or
                        availability of any listing. Prices, descriptions, availability, and
                        property details may change without notice.
                    </Typography>
                    <Typography component={"p"}>
                        Users are solely responsible for verifying all information directly
                        with property owners, agents, or other relevant parties.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        3. No Legal, Financial, or Professional Advice
                    </Typography>
                    <Typography component={"p"}>
                        Information provided on the Website is for general informational
                        purposes only and does not constitute legal advice, financial or
                        investment advice, or real estate appraisal or valuation.
                    </Typography>
                    <Typography component={"p"}>
                        You should consult qualified professionals before making any real
                        estate decisions.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        4. User Accounts (if applicable)
                    </Typography>
                    <Typography component={"p"}>
                        If the Website allows account creation:
                    </Typography>
                    <ul>
                        <li>
                            You are responsible for maintaining the confidentiality of your
                            account credentials.
                        </li>
                        <li>
                            You are responsible for all activities performed under your
                            account.
                        </li>
                        <li>
                            We reserve the right to suspend or terminate accounts that violate
                            these Terms.
                        </li>
                    </ul>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        5. Notifications and Communications
                    </Typography>
                    <Typography component={"p"}>
                        By subscribing to notifications, you agree that we may send you
                        emails or in-app notifications about new or updated listings and
                        alerts based on your saved searches or preferences.
                    </Typography>
                    <Typography component={"p"}>
                        You may unsubscribe from notifications at any time through your
                        account settings or via the provided unsubscribe mechanism.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        6. PDF Reports
                    </Typography>
                    <Typography component={"p"}>
                        Generated PDF reports are based on data available at the time of
                        generation and are provided “as is”.
                    </Typography>
                    <Typography component={"p"}>
                        PDF reports may not reflect the most current information and may not
                        be used as official valuation, legal documentation, or evidence.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        7. Intellectual Property
                    </Typography>
                    <Typography component={"p"}>
                        All Website content, including design, text, graphics, logos, and
                        software, is the property of Relapka or its licensors.
                    </Typography>
                    <Typography component={"p"}>
                        You may use the Website for personal, non-commercial purposes only.
                        You may not copy, distribute, modify, or exploit Website content
                        without prior written consent.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        8. Prohibited Use
                    </Typography>
                    <ul>
                        <li>Use the Website for unlawful or fraudulent purposes</li>
                        <li>Scrape, harvest, or extract data without permission</li>
                        <li>Interfere with Website functionality or security</li>
                        <li>Upload malicious code or attempt unauthorized access</li>
                    </ul>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        9. Limitation of Liability
                    </Typography>
                    <Typography component={"p"}>
                        To the maximum extent permitted by law, we shall not be liable for
                        any direct, indirect, incidental, or consequential damages arising
                        from the use of the Website.
                    </Typography>
                    <Typography component={"p"}>
                        We are not responsible for losses resulting from reliance on listing
                        information or third-party content.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        10. Third-Party Links
                    </Typography>
                    <Typography component={"p"}>
                        The Website may contain links to third-party websites. We are not
                        responsible for their content, policies, or practices.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        11. Data Protection and Privacy
                    </Typography>
                    <Typography component={"p"}>
                        Personal data is processed in accordance with our Privacy Policy,
                        which forms an integral part of these Terms.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        12. Changes to the Terms
                    </Typography>
                    <Typography component={"p"}>
                        We reserve the right to update or modify these Terms at any time.
                        Continued use of the Website after changes constitutes acceptance of
                        the updated Terms.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        13. Governing Law and Jurisdiction
                    </Typography>
                    <Typography component={"p"}>
                        These Terms shall be governed by and construed in accordance with the
                        laws of Georgia. Any disputes shall be subject to the exclusive
                        jurisdiction of the courts of Georgia.
                    </Typography>
                </Box>

                <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                        14. Contact Information
                    </Typography>
                    <Typography component={"p"}>
                        If you have questions regarding these Terms, please contact us:
                    </Typography>
                    <Typography>
                        Email: <strong>admin@relapka.com</strong>
                    </Typography>
                    <Typography>
                        Website: <strong>https://relapka.com</strong>
                    </Typography>
                </Box>
            </Container>
        </>
    );
};

